import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, sendEmailVerification, signOut } from 'firebase/auth';
import { collection, doc, setDoc, getDoc, getDocs} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Preloader from '../components/Preloader';

function SignUp() {
  // New state for institute selection and fetched institute list
  const [institutes, setInstitutes] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState('');

  // Existing states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [universityId, setUniversityId] = useState('');
  const [invitationType, setInvitationType] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [subscriptionAcknowledged, setSubscriptionAcknowledged] = useState(false);
  // Derived password requirement checks (live as user types)
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasDigit = /\d/.test(password);
  const hasLen   = password.length >= 6;

  const [loadingState, setLoadingState] = useState(false);

  const navigate = useNavigate();

  // Fetch institutes from Firestore on component mount
  useEffect(() => {
    const fetchInstitutes = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'institutes'));
        const instituteList = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setInstitutes(instituteList);
      } catch (error) {
        console.error('Error fetching institutes:', error);
      }
    };

    fetchInstitutes();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setErrorMessage('');

    // Validate institution selection
    if (!selectedInstitution) {
      setErrorMessage('Please select an institution.');
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setErrorMessage("Passwords don't match");
      return;
    }

    // Validate password complexity
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/;
    if (!passwordRegex.test(password)) {
      setErrorMessage(
        'Password must be at least 6 characters long and include uppercase letters, lowercase letters, and digits.'
      );
      return;
    }

    if (!termsAccepted) {
      setErrorMessage('Please accept the Terms and Conditions.');
      return;
    }

    if (!subscriptionAcknowledged) {
      setErrorMessage('Please acknowledge that we will charge a $4.99 USD semester subscription later.');
      return;
    }

    // If the user is a student, validate universityId
    if (role === 'student') {
      if (!universityId) {
        setErrorMessage('University/College User ID is required for students.');
        return;
      }
      const universityIdRegex = /^\d+$/;
      if (!universityIdRegex.test(universityId)) {
        setErrorMessage('University/College User ID must contain only digits.');
        return;
      }
    }

    // If the user is a professor, validate invitation fields
    if (role === 'professor') {
      if (!invitationType) {
        setErrorMessage('Invitation type is required for professors.');
        return;
      }
      if (!inviteCode) {
        setErrorMessage('Invitation code is required for professors.');
        return;
      }

      try {
        const invitationDocRef = doc(db, 'invitation_codes', invitationType);
        const invitationDoc = await getDoc(invitationDocRef);
        if (!invitationDoc.exists()) {
          setErrorMessage('Invalid invitation type.');
          return;
        }

        const codes = invitationDoc.data().codes || [];
        if (!codes.includes(inviteCode.trim())) {
          setErrorMessage('Invalid invitation code.');
          return;
        }
      } catch (err) {
        console.error('Error verifying invitation code:', err);
        setErrorMessage('Error verifying invitation code.');
        return;
      }
    }

    // If a specific institute is selected (not "other"), verify email domain (allowing subdomains)
    if (selectedInstitution) {
      const institute = institutes.find((inst) => inst.id === selectedInstitution);
      const emailDomain = email.split('@')[1]?.toLowerCase();
      const domains = Array.isArray(institute?.domains) ? institute.domains : [];

      const ok = emailDomain && domains.some((d) => {
        const dom = d.toLowerCase().trim();
        return emailDomain === dom || emailDomain.endsWith(`.${dom}`);
      });

      if (!ok) {
        setErrorMessage('Email domain does not match the selected institution.');
        setLoadingState(false);
        return;
      }
    }

    setLoadingState(true);

    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Prepare user data to store in Firestore
      const userData = {
        firstName,
        lastName,
        role,
        email,
        createdAt: new Date(),
        termsAccepted,
        institution: selectedInstitution, // Either a specific institute id or "other"
      };

      if (role === 'student') {
        userData.universityId = universityId;
      }
      if (role === 'professor') {
        userData.invitationType = invitationType;
        userData.invitationCode = inviteCode.trim();
      }

      // Store user info in Firestore under "users" collection
      await setDoc(doc(db, 'users', firebaseUser.uid), userData);

      // If an actual institute (not "other") is selected, send email verification
      const actionCodeSettings = {
        url: window.location.origin + '/sign-in',
        handleCodeInApp: true,
      };
      await sendEmailVerification(firebaseUser, actionCodeSettings);
      alert('A verification email has been sent. Please check your inbox.');      

      // Clear form fields
      setFirstName('');
      setLastName('');
      setRole('');
      setUniversityId('');
      setInvitationType('');
      setInviteCode('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setErrorMessage('');
      setSelectedInstitution('');

      // Redirect to Sign In page
      await signOut(auth);
      navigate('/sign-in');
    } catch (error) {
      console.error('Error creating user:', error);
      setErrorMessage(error.message.replace('firebase', 'CoursEngage'));
    } finally {
      setLoadingState(false);
    }
  };

  if (loadingState) {
    return <Preloader />;
  }


  return (
    <main
        className="min-h-screen"
        style={{
          backgroundColor: "var(--bg-color)",
          color: "var(--text-color)",
        }}
      >
    <section
      className="flex item-center justify-center px-4 pt-20"
      style={{ backgroundColor: 'var(--bg-color)', color: 'var(--text-color)' }}
    >
      <div
        className="w-full max-w-md p-8 rounded-2xl shadow-2xl"
        style={{ backgroundColor: 'var(--card-bg)' }}
      >
        <h1 className="text-3xl font-bold text-center mb-6">Sign Up</h1>
        
        {/* Institution Dropdown */}
        <div className="mb-4">
          <label htmlFor="institution" className="block mb-1 text-sm font-medium">
            Select Institution
          </label>
          <select
            id="institution"
            className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                       bg-[var(--input-bg)] text-[var(--text-color)]
                       focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                       focus:border-transparent transition-all"
            value={selectedInstitution}
            onChange={(e) => setSelectedInstitution(e.target.value)}
            required
          >
            <option value="" disabled hidden>
              Choose your institution
            </option>
            {institutes.map((inst) => (
              <option key={inst.id} value={inst.id}>
                {inst.name}
              </option>
            ))}
            {/* <option value="other">Other</option> */}
          </select>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* First Name */}
          <div>
            <label htmlFor="first_name" className="block mb-1 text-sm font-medium">
              First Name
            </label>
            <input
              type="text"
              id="first_name"
              placeholder="First Name"
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                         bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="last_name" className="block mb-1 text-sm font-medium">
              Last Name
            </label>
            <input
              type="text"
              id="last_name"
              placeholder="Last Name"
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                         bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>

          {/* Role */}
          <div>
            <label htmlFor="role" className="block mb-1 text-sm font-medium">
              You are a...
            </label>
            <select
              id="role"
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                         bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              required
            >
              <option value="" disabled hidden></option>
              <option value="student">Student</option>
              <option value="professor">Professor</option>
            </select>
          </div>

          {/* Student-specific: University/College User ID */}
          {role === 'student' && (
            <div>
              <label htmlFor="university_id" className="block mb-1 text-sm font-medium">
                University/College User ID
              </label>
              <input
                type="text"
                id="university_id"
                placeholder="University/College User ID"
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                           bg-[var(--input-bg)] text-[var(--text-color)]
                           focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                           focus:border-transparent transition-all"
                value={universityId}
                onChange={(e) => setUniversityId(e.target.value)}
                required
              />
              <p className="text-xs text-[var(--muted-color)] mt-1">
                * Must contain only digits
              </p>
            </div>
          )}

          {/* Professor-specific: Invitation Type & Code */}
          {role === 'professor' && (
            <>
              <div>
                <label htmlFor="invitation_type" className="block mb-1 text-sm font-medium">
                  Invitation Type
                </label>
                <select
                  id="invitation_type"
                  className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                             bg-[var(--input-bg)] text-[var(--text-color)]
                             focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                             focus:border-transparent transition-all"
                  value={invitationType}
                  onChange={(e) => setInvitationType(e.target.value)}
                  required
                >
                  <option value="" disabled hidden></option>
                  <option value="promo">Promo</option>
                  <option value="enterprise">Enterprise</option>
                  {/* Add more invitation types as needed */}
                </select>
              </div>

              <div>
                <label htmlFor="invite_code" className="block mb-1 text-sm font-medium">
                  Invitation Code
                </label>
                <input
                  type="text"
                  id="invite_code"
                  placeholder="Invitation Code"
                  className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                             bg-[var(--input-bg)] text-[var(--text-color)]
                             focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                             focus:border-transparent transition-all"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  required
                />
                <p className="text-xs text-[var(--muted-color)] mt-1">
                  Please enter your invitation code to proceed.
                </p>
              </div>
            </>
          )}

          {/* Email */}
          <div>
            <label htmlFor="email" className="block mb-1 text-sm font-medium">
              Email address
            </label>
            <input
              type="email"
              id="email"
              placeholder="Email address"
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                         bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block mb-1 text-sm font-medium">
              Password
            </label>
            <input
              type="password"
              id="password"
              placeholder="Password"
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                         bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div className="mt-1 text-xs space-y-1" role="status" aria-live="polite">
            <div className={hasLen ? "text-green-600" : "text-[var(--muted-color)]"}>
              {hasLen ? "✓" : "•"} At least 6 characters
            </div>
            <div className={hasUpper ? "text-green-600" : "text-[var(--muted-color)]"}>
              {hasUpper ? "✓" : "•"} Includes an uppercase letter (A-Z)
            </div>
            <div className={hasLower ? "text-green-600" : "text-[var(--muted-color)]"}>
              {hasLower ? "✓" : "•"} Includes a lowercase letter (a-z)
            </div>
            <div className={hasDigit ? "text-green-600" : "text-[var(--muted-color)]"}>
              {hasDigit ? "✓" : "•"} Includes a digit (0-9)
            </div>
          </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirm_password" className="block mb-1 text-sm font-medium">
              Password Confirmation
            </label>
            <input
              type="password"
              id="confirm_password"
              placeholder="Password Confirmation"
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md
                         bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="terms"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mr-2"
              required
            />
            <label htmlFor="terms" className="text-sm">
              I agree to the{' '}
              <button
                type="button"
                onClick={() => setShowTermsModal(true)}
                className="text-[var(--primary-color)] underline"
              >
                Terms and Conditions
              </button>
            </label>
          </div>

          {/* Subscription Acknowledgement */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="subscription_ack"
              checked={subscriptionAcknowledged}
              onChange={e => setSubscriptionAcknowledged(e.target.checked)}
              className="flex-shrink-0 h-5 w-5 mt-0.5"
            />
            <label htmlFor="subscription_ack" className="text-sm leading-tight">
              I acknowledge that this service costs $4.99 USD per semester for students and will be charged later during the semester.
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full py-2 mt-2 rounded-md font-semibold
                       bg-[var(--primary-color)] text-white
                       hover:bg-[var(--hover-color)]
                       transition-colors"
          >
            Create account
          </button>

          {/* Error Message */}
          {errorMessage && (
            <div className="text-red-500 text-center mt-2">{errorMessage}</div>
          )}
        </form>

        <p className="text-center mt-4">
          Already have an account?{' '}
          <a
            href="/sign-in"
            className="text-[var(--primary-color)] hover:underline"
          >
            Sign In
          </a>
        </p>
      </div>

      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg shadow-lg max-w-2xl mx-4">
            <h2 className="text-2xl font-bold mb-4">TERMS AND CONDITIONS</h2>
            <p className="text-sm italic mb-4">Last updated: [May 12, 2025]</p>
            <p className="text-sm italic mb-6">
              Note: The operator of this Service ("CoursEngage") is not yet formally incorporated or registered as a legal entity.
              These Terms nonetheless govern your use of the Service as soon as it is made available.
            </p>
            <div className="max-h-96 overflow-y-auto text-sm space-y-4 mb-4">
              <div>
                <strong>1. Definitions</strong><br/>
                1.1 “Company,” “we,” “us,” or “our” means the unregistered operator of the Service, with contact information in Section 17.<br/>
                1.2 “Service” means the website, APIs, mobile applications, dashboards and all related features, content, or functionality offered by Company.<br/>
                1.3 “User,” “you,” or “your” means any person or entity accessing or using the Service.<br/>
                1.4 “Content” means all text, graphics, images, files, information and other data you submit to or access through the Service.
              </div>
              <div>
                <strong>2. Acceptance of Terms</strong><br/>
                By accessing or using the Service in any way, you agree to be bound by these Terms. If you do not agree, do not use the Service.
              </div>
              <div>
                <strong>3. Eligibility & Minors</strong><br/>
                3.1 General Age Requirements:<br/>
                &nbsp;&nbsp;• Children (under 13): You may not register for or use the Service without verifiable parental or legal guardian consent. If we learn that we have collected personal information from a child under 13 without such consent, we will delete it promptly.<br/>
                &nbsp;&nbsp;• Minors (ages 13-17): You may use the Service only with the consent of a parent or legal guardian. By registering or using the Service, you represent that you have obtained any necessary permissions.<br/>
                3.2 Adults: If you are 18 years or older (or the age of majority in your jurisdiction), you may use the Service without additional permissions.
              </div>
              <div>
                <strong>4. Account Registration & Security</strong><br/>
                4.1 When you register, you must provide accurate, complete, and current information.<br/>
                4.2 You are responsible for maintaining the confidentiality of your login credentials. We do not have the ability to see or retrieve your password; it is securely managed by Firebase Authentication.<br/>
                4.3 Notify us immediately of any unauthorized use or breach of your account.
              </div>
              <div>
                <strong>5. User Conduct</strong><br/>
                You agree not to:<br/>
                &nbsp;&nbsp;- Violate any applicable law, regulation or third‑party right.<br/>
                &nbsp;&nbsp;- Reverse‑engineer, decompile, or attempt to extract source code.<br/>
                &nbsp;&nbsp;- Interfere with or disrupt the Service or servers.<br/>
                &nbsp;&nbsp;- Transmit malware, viruses, or harmful code.<br/>
                &nbsp;&nbsp;- Use the Service to send unsolicited communications (spam).
              </div>
              <div>
                <strong>6. Content Ownership & License</strong><br/>
                6.1 Your Content. You retain ownership of Content you submit. By submitting Content, you grant Company a worldwide, royalty‑free, sublicensable license to store, reproduce, modify, publish and display it solely to provide the Service.<br/>
                6.2 Company Content. All materials provided by Company (including logos, designs, software) are owned by or licensed to Company and are protected by intellectual‑property laws.
              </div>
              <div>
                <strong>7. Data Retention & Access</strong><br/>
                7.1 We take reasonable measures to protect your information; however, in the event of a data leak or breach, we cannot be held liable for any consequences that may arise (see Section 11).<br/>
                7.2 Your access to data for a given class will end one week after the class‑end date you specify. After that, you will no longer be able to view, retrieve, or use any previously available data.<br/>
                7.3 We only collect data directly related to the tool or activities performed. We do not actively track your activities on the platform; we only store the email addresses you provide.
              </div>
              <div>
                <strong>8. Privacy & Children’s Online Privacy</strong><br/>
                8.1 Our Privacy Policy explains how we collect, use and protect your personal information. By using the Service, you consent to those practices.<br/>
                8.2 In compliance with the Children’s Online Privacy Protection Act (COPPA), we do not knowingly collect personal information from children under 13 without verifiable parental consent.
              </div>
              <div>
                <strong>9. Fees & Payment (if applicable)</strong><br/>
                If you purchase any paid features, you agree to pay all fees displayed at checkout. All payments are non‑refundable except as required by law. We may change our fees upon 30 days’ notice.
              </div>
              <div>
                <strong>10. Term, Suspension & Termination</strong><br/>
                10.1 Term. These Terms begin when you first access the Service and continue until terminated.<br/>
                10.2 Suspension/Termination. We may suspend or terminate your access at any time, without notice, for violation of these Terms or if we believe your use threatens security or operations.<br/>
                10.3 Effect of Termination. Upon termination, your right to use the Service ends immediately; any licenses you granted survive.
              </div>
              <div>
                <strong>11. Disclaimer of Warranties</strong><br/>
                THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE,” WITH ALL FAULTS AND WITHOUT WARRANTY OF ANY KIND. TO THE FULLEST EXTENT PERMITTED BY LAW, COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE AND NON‑INFRINGEMENT.
              </div>
              <div>
                <strong>12. Limitation of Liability</strong><br/>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL COMPANY OR ITS AFFILIATES, OFFICERS, DIRECTORS OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, OR FOR LOSS OF PROFITS, REVENUE, DATA OR BUSINESS, EVEN IF ADVISED OF THE POSSIBILITY.<br/>
                <em>Our total aggregate liability for all claims will not exceed the greater of (a) the total fees you paid in the 12 months before the claim, or (b) USD 100.</em>
              </div>
              <div>
                <strong>13. Indemnification</strong><br/>
                You agree to indemnify and hold harmless Company and its officers, directors, employees and agents from any claims, losses or expenses (including reasonable attorneys’ fees) arising out of your use of the Service or violation of these Terms.
              </div>
              <div>
                <strong>14. Governing Law & Disputes</strong><br/>
                These Terms and any disputes will be governed by the laws of the State of Nebraska, without regard to conflicts‑of‑law principles. You and Company submit to the exclusive jurisdiction of the state and federal courts located in Nebraska, United States.
              </div>
              <div>
                <strong>15. Changes to These Terms</strong><br/>
                We may revise these Terms at any time. We’ll post the “Last updated” date at the top and, for material changes, notify you by email or in‑app notice. Continued use after the effective date means you accept the new Terms.
              </div>
              <div>
                <strong>16. Severability & Waiver</strong><br/>
                If any provision is held invalid, that provision is struck and the remainder remains in full force. No waiver of any term is a continuing waiver.
              </div>
              <div>
                <strong>17. Contact Information</strong><br/>
                If you have questions or notices under these Terms, contact us at:<br/>
                Email: support@coursengage.com<br/>
                {/* Address: [Street Address, City, State, ZIP, Country] */}
              </div>
            </div>
            <button
              onClick={() => setShowTermsModal(false)}
              className="w-full py-2 mt-2 rounded-md font-semibold bg-[var(--primary-color)] text-white hover:bg-[var(--hover-color)] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

    </section>
    </main>
  );
}

export default SignUp;
